import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaSave, FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { generateUSAFormationPDF } from "./pdfGenerator";
import companyLogo from "../../Assets/BooksCompl.png";

const QuestionForm = ({
  onClose, onSubmit, initialAnswers, initialComments, isSubmitted, companyType = "LLC"
}) => {
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [comments, setComments] = useState(initialComments || {});
  const [expanded, setExpanded] = useState({});
  const [selectedBusinessType, setSelectedBusinessType] = useState(companyType);
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  const cCorpQuestions = {
    "Basic Information": [
      "What Type of Business Would You Like To Start? (LLC or C-Corp)",
      "In which U.S. state do you want to incorporate the company?",
      "What Would You Like Your Business Name To Be?",
      "Business Purpose (Be Specific)",
      "Physical Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)",
      "Mailing Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - President": [
      "First Name",
      "Last Name",
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - Secretary": [
      "First Name", 
      "Last Name",
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - Treasurer": [
      "First Name",
      "Last Name", 
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)",
    ],
    "Shares & Other Informations": [
      "Number of Authorized Shares",
      "Par Value",
      "All officers are directors"
    ],
    "Responsible Party": [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number (USA)",
      "India Address - Line1",
      "India Address - Line2", 
      "City",
      "State",
      "Zipcode",
      "Country",
      "You have, or you expect to have, any employees who will receive Forms W-2 in the next 12 months. (Yes or No)",
      "Fiscal Year-End Month"
    ]
  };

  const llcQuestions = {
    "Basic Information": [
      "What Type of Business Would You Like To Start? (LLC or C-Corp)",
      "In which U.S. state do you want to incorporate the company?",
      "What Would You Like Your Business Name To Be?",
      "Business Purpose (Be Specific)",
      "Physical Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)",
      "Mailing Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Partners Information - Partner 1": [
      "First Name",
      "Last Name",
      "Percentage Holding"
    ],
    "Partners Information - Partner 2": [
      "First Name",
      "Last Name", 
      "Percentage Holding"
    ],
    "Partners Information - Partner 3": [
      "First Name",
      "Last Name",
      "Percentage Holding"
    ],
    "Responsible Party": [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number (USA)",
      "India Address - Line1",
      "India Address - Line2",
      "City", 
      "State",
      "Zipcode",
      "Country",
      "You have, or you expect to have, any employees who will receive Forms W-2 in the next 12 months. (Yes or No)"
    ]
  };

  const formQuestions = selectedBusinessType === "C-Corp" ? cCorpQuestions : llcQuestions;

  const totalQuestions = Object.values(formQuestions).reduce(
    (total, section) => total + section.length,
    0
  );

  const handleAnswer = (index, value) => {
    if (!isSubmitted) {
      setAnswers((prev) => ({ ...prev, [index]: value }));
    }
  };

  const handleComment = (index, value) => {
    if (!isSubmitted) {
      setComments((prev) => ({ ...prev, [index]: value }));
    }
  };

  const toggleQuestion = (question) => {
    if (!isSubmitted) {
      setExpanded((prev) => ({ ...prev, [question]: !prev[question] }));
    }
  };

  const calculateProgress = () => {
    return Object.keys(answers).filter((k) => answers[k] !== undefined).length;
  };

  const calculateSectionProgress = (section) => {
    return formQuestions[section].filter((_, index) => answers[`${section}-${index}`] !== undefined && answers[`${section}-${index}`] !== "comment").length;
  };

  const handleSave = () => {
    if (!isSubmitted) {
      const currentProgress = calculateProgress();
      onSubmit({ 
        answers: { 
          ...answers,
          'businessType': selectedBusinessType // Ensure business type is always included
        }, 
        comments 
      }, false);
      alert(`Saved progress: ${currentProgress}/${totalQuestions} questions answered`);
    }
  };

  const handleConfirm = async () => {
    const currentProgress = calculateProgress();
    if (currentProgress < 1) {
      alert("Please answer all questions.");
      return;
    }

    if (!isSubmitted) {
      try {
        const pdfBlob = generateUSAFormationPDF(
          formQuestions,
          answers,
          comments,
          selectedBusinessType,
          companyLogo
        );

        const pdfFormData = new FormData();
        const pdfName = `USA Company Formation Checklist - ${selectedBusinessType}.pdf`;
        pdfFormData.append('pdf', pdfBlob, pdfName);

        const pdfToken = localStorage.getItem("token");
        const pdfResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-pdf`, {
          method: "POST",
          body: pdfFormData,
          headers: {
            Authorization: `Bearer ${pdfToken}`,
          },
        });

        if (!pdfResponse.ok) {
          throw new Error("Failed to upload PDF");
        }

        const pdfResult = await pdfResponse.json();

        onSubmit({
          answers: {
            ...answers,
            'businessType': selectedBusinessType // Ensure business type is always included
          },
          comments,
          pdfUrl: pdfResult.pdfUrl
        }, true);

        alert("Form submitted successfully!");
      } catch (error) {
        console.error("Error in form submission:", error);
        alert(`Failed to submit form: ${error.message}`);
      }
    }
  };

  const renderQuestionInput = (questionTitle, subIndex, subQuestion) => {
    const questionKey = `${questionTitle}-${subIndex}`;
    
    // Special handling for business type selection
    if (subQuestion.includes("What Type of Business Would You Like To Start?")) {
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {["LLC", "C-Corp"].map((option) => (
            <button
              key={option}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                answers[questionKey] === option 
                  ? 'bg-primary-500 text-white shadow-md' 
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
              } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
                setSelectedBusinessType(option);
              }}
              disabled={isSubmitted}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    // Special handling for Yes/No questions
    if (subQuestion.includes("Yes or No") || subQuestion.includes("employees who will receive Forms W-2")) {
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {["Yes", "No"].map((option) => (
            <button
              key={option}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                answers[questionKey] === option 
                  ? 'bg-primary-500 text-white shadow-md' 
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
              } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
              }}
              disabled={isSubmitted}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    // Special handling for address selection questions
    if (subQuestion.includes("Our Registered Agent Address or Your Mailing Address")) {
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {["Our Registered Agent Address", "Your Mailing Address"].map((option) => (
            <button
              key={option}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                answers[questionKey] === option 
                  ? 'bg-primary-500 text-white shadow-md' 
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
              } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
              }}
              disabled={isSubmitted}
            >
              {option}
            </button>
          ))}
          <button
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              answers[questionKey] === "comment" 
                ? 'bg-orange-500 text-white shadow-md' 
                : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
            } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleAnswer(questionKey, "comment");
            }}
            disabled={isSubmitted}
          >
            CUSTOM
          </button>
        </div>
      );
    }

    // Default comment input for all other questions
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            answers[questionKey] === "comment" 
              ? 'bg-orange-500 text-white shadow-md' 
              : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
          } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleAnswer(questionKey, "comment");
          }}
          disabled={isSubmitted}
        >
          COMMENT
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (!modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [modalRoot]);

  if (!modalRoot) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-scale-in">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-dark-800 dark:text-white">
              USA Company Formation Checklist
            </h2>
            <div className="flex items-center gap-3">
              <span className="px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold text-sm">
                {calculateProgress()}/{totalQuestions}
              </span>
              <button 
                onClick={onClose} 
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-gray-400 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/30 text-sm text-primary-700 dark:text-primary-300">
            Please complete the checklist carefully. Our team will review your submission and guide you through the remaining steps.
          </div>
        </div>

        {/* Questions Container */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {Object.keys(formQuestions).map((questionTitle, index) => {
            const sectionLetter = String.fromCharCode(65 + index);
            const sectionProgress = calculateSectionProgress(questionTitle);
            const isExpanded = expanded[questionTitle];
            return (  
              <div 
                key={index} 
                className={`rounded-xl border transition-all ${
                  isExpanded 
                    ? 'border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10' 
                    : 'border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800'
                }`}
              >
                <div 
                  onClick={() => toggleQuestion(questionTitle)}
                  className="flex items-center justify-between p-4 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-primary-500 text-white flex items-center justify-center text-sm font-bold">
                      {sectionLetter}
                    </span>
                    <span className="font-semibold text-dark-800 dark:text-white">{questionTitle}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400">
                      {sectionProgress}/{formQuestions[questionTitle].length}
                    </span>
                    {isExpanded ? (
                      <FaChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <FaChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-4">
                    {formQuestions[questionTitle].map((subQuestion, subIndex) => (
                      <div key={subIndex} className="p-4 rounded-xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700">
                        <p className="text-sm text-dark-800 dark:text-gray-300 mb-2">
                          <span className="font-semibold text-primary-600 dark:text-primary-400 mr-2">{subIndex + 1}.</span>
                          {subQuestion}
                        </p>
                        {subQuestion.includes("President") && subQuestion.includes("First Name") && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
                            * Note: The President is the operations manager, in charge of day to day operations.
                          </p>
                        )}
                        {subQuestion.includes("Secretary") && subQuestion.includes("First Name") && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
                            * Note: The Secretary documents corporate transactions like amendments, annual reports, and licenses.
                          </p>
                        )}
                        {subQuestion.includes("Treasurer") && subQuestion.includes("First Name") && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
                            * Note: The Treasurer is in charge of financial transactions.
                          </p>
                        )}
                        {renderQuestionInput(questionTitle, subIndex, subQuestion)}
                        {(answers[`${questionTitle}-${subIndex}`] === "comment" || 
                          answers[`${questionTitle}-${subIndex}`] === "Your Mailing Address") && (
                          <textarea
                            placeholder="Add your response..."
                            value={comments[`${questionTitle}-${subIndex}`] || ""}
                            onChange={(e) => handleComment(`${questionTitle}-${subIndex}`, e.target.value)}
                            className="w-full mt-3 p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 resize-none"
                            rows={3}
                            disabled={isSubmitted}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <button 
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200"
          >
            Close
          </button>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {!isSubmitted && (
              <>
                <button
                  onClick={handleSave}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all duration-200"
                >
                  <FaSave className="w-4 h-4" />
                  Save Progress
                </button>
                <button
                  onClick={handleConfirm}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 transition-all duration-200"
                >
                  <FaCheck className="w-4 h-4" />
                  Submit
                </button>
              </>
            )}
            {isSubmitted && (
              <span className="px-6 py-3 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold w-full text-center">
                Form Submitted
              </span>
            )}
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default QuestionForm;